import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { PhoneInputField, isValidPhoneNumber } from '../PhoneInputField';
import { PhoneSupportNotice } from '../PhoneSupportNotice';
import { sendSupportForm } from '../../../lib/use-cases/send-support-form';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { disableScroll, enableScroll } from '../../shared/scroll';
import { useRouting } from '../../shared/use-routing';
import { buildSupportReason } from '../../shared/build-support-reason';
import { getDefaultPhoneCountry } from '../../../i18n/i18n';
import styles from './CallMeBackForm.css';

function CallMeBackForm({ onClose, email }) {
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [isFail, setIsFail] = useState(false);
    const { lang } = useRouting();

    useEffect(() => {
        disableScroll();

        return () => enableScroll();
    }, []);

    const handlePhoneChange = (value) => {
        if (phoneError) setPhoneError('');
        setPhone(value || '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!phone) {
            setPhoneError('Please enter your phone number');

            return;
        }

        if (!isValidPhoneNumber(phone)) {
            setPhoneError('Please enter a valid phone number');

            return;
        }

        try {
            setIsPending(true);

            logEvent(EVENT_TYPES.CALL_ME_BACK_SUBMITTED, { 'target_click_block': 'dashboard_protected' });

            await sendSupportForm({
                email,
                reason: buildSupportReason('Call me back', lang),
                message: `Phone: ${phone}`
            });

            setIsSuccessful(true);
        } catch {
            setIsFail(true);
        } finally {
            setIsPending(false);
        }
    };

    const renderContent = () => {
        if (isFail) {
            return (
                <>
                    <div className={styles.noticeError} role='alert'>
                        <Icon name='alertTriangle' className={styles.noticeIcon} />
                        <div>
                            <span className={styles.noticeTitle}>Request not sent</span>
                            <span>Please try again later, or call us directly.</span>
                        </div>
                    </div>
                    <div className={styles.buttons}>
                        <Button
                            label='Try again'
                            className={styles.button}
                            onClick={() => setIsFail(false)}
                        />
                        <Button
                            label='Close'
                            className={styles.button}
                            type='secondary'
                            onClick={onClose}
                        />
                    </div>
                </>
            );
        }

        if (isSuccessful) {
            return (
                <>
                    <div className={styles.noticeSuccess} role='status'>
                        <Icon name='check' className={styles.noticeIcon} />
                        <div>
                            <span className={styles.noticeTitle}>Request received</span>
                            <span>We will call you back shortly.</span>
                        </div>
                    </div>
                    <Button
                        label='Close'
                        className={styles.button}
                        onClick={onClose}
                    />
                </>
            );
        }

        return (
            <>
                <p className={styles.description}>
                    Leave your phone number and our specialist will call you back as soon as possible.
                </p>
                <PhoneSupportNotice withComingSoon />
                <PhoneInputField
                    id='phone'
                    label='Your phone number (required)'
                    value={phone}
                    onChange={handlePhoneChange}
                    errorText={phoneError}
                    defaultCountry={getDefaultPhoneCountry(lang)}
                />
                <Button
                    label='Request a call'
                    buttonProps={{ type: 'submit' }}
                    className={styles.button}
                    isLoading={isPending}
                />
            </>
        );
    };

    return (
        <div className={styles.root} role='dialog' aria-label='Call me back'>
            <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.header}>
                    <h1 className={styles.title}>Call me back</h1>
                    <Button
                        type='secondary'
                        iconLeft={{ name: 'close' }}
                        onClick={onClose}
                        size='small'
                    />
                </div>
                {renderContent()}
            </form>
        </div>
    );
}

CallMeBackForm.propTypes = {
    onClose: PropTypes.func.isRequired,
    email: PropTypes.string.isRequired
};

export {
    CallMeBackForm
};
